import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { GraduationCap, Loader2, User, Building2 } from "lucide-react";

export default function Register() {
  const { t, lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", preferred_language: lang, role: "parent", school_id: "" });
  const [schools, setSchools] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.school_id) {
      setError(t("select_school"));
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) navigate(res.user.role === "principal" ? "/billing" : "/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto flex max-w-md flex-col px-5 py-14">
        <div className="mb-8 text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-3xl bg-secondary text-secondary-foreground">
            <GraduationCap className="h-7 w-7" strokeWidth={1.75} />
          </span>
          <h1 className="mt-5 font-heading text-3xl font-semibold text-foreground">{t("register_title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("register_subtitle")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-3xl border border-black/5 bg-card p-7 shadow-sm">
          {error && (
            <div data-testid="register-error" className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {error}
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("field_account_type")}</label>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: "parent", l: t("role_parent"), icon: User }, { v: "principal", l: t("role_principal"), icon: Building2 }].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`register-role-${opt.v}`}
                  onClick={() => setForm((f) => ({ ...f, role: opt.v }))}
                  className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors ${
                    form.role === opt.v
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  <opt.icon className="h-4 w-4" strokeWidth={1.75} /> {opt.l}
                </button>
              ))}
            </div>
            {form.role === "principal" && (
              <p className="mt-2 text-xs text-muted-foreground">{t("principal_note")}</p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("field_name")}</label>
            <input
              data-testid="register-name"
              required
              value={form.name}
              onChange={update("name")}
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("field_school")}</label>
            <select
              data-testid="register-school"
              required
              value={form.school_id}
              onChange={update("school_id")}
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            >
              <option value="">{t("select_school")}</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("field_email")}</label>
            <input
              data-testid="register-email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("field_password")}</label>
            <input
              data-testid="register-password"
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={update("password")}
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("field_language")}</label>
            <div className="flex gap-2">
              {[{ v: "en", l: "English" }, { v: "es", l: "Español" }].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`register-lang-${opt.v}`}
                  onClick={() => setForm((f) => ({ ...f, preferred_language: opt.v }))}
                  className={`flex-1 rounded-2xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
                    form.preferred_language === opt.v
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          <Button
            data-testid="register-submit"
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-full bg-primary text-base font-semibold text-primary-foreground hover:bg-primary-hover"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("btn_register")}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t("have_account")}{" "}
          <Link data-testid="go-login" to="/login" className="font-semibold text-primary hover:underline">
            {t("nav_login")}
          </Link>
        </p>
      </div>
    </div>
  );
}
